/**
 * Language catalog, English-hub translation rules, validation and status
 * derivation for live captioning.
 *
 * The captions API's rules (the brief) are encoded here and always apply:
 *   - `source` is the spoken language: any Speechmatics realtime language;
 *   - `langs` lists up to 4 unique caption tracks; the source track is speech
 *     recognition, every other track is a machine translation of it;
 *   - translation is to or from English only: `en` translates to any
 *     `translate` language, a non-English `translate` language to `en` only,
 *     and a transcribe-only language publishes captions in itself alone;
 *   - the profanity filter only works for `en`, `es` and `it` sources.
 *
 * Core's `GET /captions/languages` ({ languages, hub, maxOutputs,
 * sourceTrackOptional, profanitySources, nonEnglishPairs }) is fetched once
 * per session and intersected with this catalog by `normalizeCatalog`: a
 * language core does not offer is not offered here either, and a language
 * only translates when both sides agree. `sourceTrackOptional` (core accepts
 * `langs` without the source), `profanitySources` and `nonEnglishPairs` are
 * taken as sent; an older core omits them, which reads as required, en/es/it
 * and English-hub only. `FALLBACK_CATALOG` is used when the endpoint is
 * missing or unreachable.
 *
 * `nonEnglishPairs: true` lifts the English-hub rule: any catalog language
 * may be a target for any spoken language, and a pair Speechmatics can't
 * translate (neither side English, or a captions-only side) is translated by
 * Gemini automatically (`needsGemini`).
 *
 * Not listed: nn (batch-only), Tagalog, and region or script variants.
 */

export const HUB_LANGUAGE = 'en';
export const LANGS_MAX = 4;
export const DELAY_MIN = 0;
export const DELAY_MAX = 30;
export const DELAY_RECOMMENDED = 8;

export const TRANSLATION_RULE = 'Translation is available to or from English.';
export const LANGUAGES_EXPLAINER = 'Captions in the spoken language are produced by speech recognition; other languages are machine-translated from it. Translation is available to or from English.';
export const LANGUAGES_EXPLAINER_ANY_PAIR = 'Captions in the spoken language are produced by speech recognition; other languages are machine-translated from it. Pairs without English are translated with Gemini.';
export const SOURCE_EXPLAINER = 'The language spoken on the stream. Languages marked "Translation available" can be translated to or from English.';
export const SOURCE_EXPLAINER_ANY_PAIR = 'The language spoken on the stream. It can be translated into any other language on the list.';
export const NON_HUB_HINT = 'Only English can be added as a translation.';
export const PROFANITY_UNAVAILABLE = 'Not available for this language.';
export const PROFANITY_SOURCES = Object.freeze(['en', 'es', 'it']);

// Speechmatics realtime, transcribe AND translate (to/from English).
const TRANSLATABLE = [
	['en', 'English'], ['bg', 'Bulgarian'], ['ca', 'Catalan'], ['cmn', 'Chinese (Mandarin)'],
	['hr', 'Croatian'], ['cs', 'Czech'], ['da', 'Danish'], ['nl', 'Dutch'], ['et', 'Estonian'],
	['fi', 'Finnish'], ['fr', 'French'], ['gl', 'Galician'], ['de', 'German'], ['el', 'Greek'],
	['hi', 'Hindi'], ['hu', 'Hungarian'], ['id', 'Indonesian'], ['it', 'Italian'], ['ja', 'Japanese'],
	['ko', 'Korean'], ['lv', 'Latvian'], ['lt', 'Lithuanian'], ['ms', 'Malay'], ['no', 'Norwegian'],
	['pl', 'Polish'], ['pt', 'Portuguese'], ['ro', 'Romanian'], ['ru', 'Russian'], ['sk', 'Slovak'],
	['sl', 'Slovenian'], ['es', 'Spanish'], ['sv', 'Swedish'], ['tr', 'Turkish'], ['uk', 'Ukrainian'],
	['vi', 'Vietnamese'],
];

// Speechmatics realtime, transcribe only: no translation in either direction.
const TRANSCRIBE_ONLY = [
	['ar', 'Arabic'], ['ba', 'Bashkir'], ['eu', 'Basque'], ['be', 'Belarusian'], ['bn', 'Bengali'],
	['yue', 'Cantonese'], ['eo', 'Esperanto'], ['he', 'Hebrew'], ['ia', 'Interlingua'], ['ga', 'Irish'],
	['mt', 'Maltese'], ['mr', 'Marathi'], ['mn', 'Mongolian'], ['fa', 'Persian'], ['sw', 'Swahili'],
	['ta', 'Tamil'], ['th', 'Thai'], ['ur', 'Urdu'], ['ug', 'Uyghur'], ['cy', 'Welsh'],
];

// Display names for codes an existing flow may carry that the catalog does not.
const EXTRA_NAMES = {
	zh: 'Chinese',
};

// Timed caption windows (seconds); `null` is unlimited.
export const DURATION_PRESETS = [
	{ value: 900, label: '15 min' },
	{ value: 3600, label: '1 hour' },
	{ value: 14400, label: '4 hours' },
	{ value: 86400, label: '24 hours' },
	{ value: null, label: 'Unlimited' },
];
export const EXTEND_PRESETS = [
	{ value: 900, label: '+15 min' },
	{ value: 1800, label: '+30 min' },
	{ value: 3600, label: '+60 min' },
];

// Worker advertising rule: a language is only listed in the master playlist
// while the runner's status file is under 45s old and that language's newest
// cue is within 60s of it.
export const PRODUCTION_MAX_AGE_MS = 45_000;
export const EDGE_MAX_LAG_MS = 60_000;
// After Start (or a restart from a settings save) the runner needs up to a
// minute to publish; until then missing production reads Starting, not
// Waiting for stream.
export const STARTING_GRACE_MS = 60_000;
export const WAITING_FOR_STREAM_HELP = 'Your encoder is offline. Captions resume automatically when it returns.';

export const FALLBACK_CATALOG = Object.freeze({
	languages: Object.freeze([
		...TRANSLATABLE.map(([code, name]) => Object.freeze({ code, name, transcribe: true, translate: true })),
		...TRANSCRIBE_ONLY.map(([code, name]) => Object.freeze({ code, name, transcribe: true, translate: false })),
	]),
	hub: HUB_LANGUAGE,
	maxOutputs: LANGS_MAX,
	sourceTrackOptional: false,
	profanitySources: PROFANITY_SOURCES,
	nonEnglishPairs: false,
});

/** Core's capability flags; absent (older core) means today's rules. */
function catalogFlags(raw) {
	return {
		sourceTrackOptional: raw?.sourceTrackOptional === true,
		nonEnglishPairs: raw?.nonEnglishPairs === true,
		profanitySources: Array.isArray(raw?.profanitySources)
			? raw.profanitySources.filter((c) => typeof c === 'string' && c).map((c) => c.toLowerCase())
			: PROFANITY_SOURCES,
	};
}

/**
 * Core's payload intersected with the API rules: only languages both offer,
 * translatable only when both say so, at most LANGS_MAX outputs, English hub.
 * A missing, empty or malformed language list yields the full fallback list.
 */
export function normalizeCatalog(raw) {
	const flags = catalogFlags(raw);
	const offered = new Map(
		(Array.isArray(raw?.languages) ? raw.languages : [])
			.filter((l) => l && typeof l.code === 'string' && l.code)
			.map((l) => [l.code.toLowerCase(), l]),
	);
	if (!offered.size) return { ...FALLBACK_CATALOG, ...flags };
	const languages = FALLBACK_CATALOG.languages
		.filter((l) => offered.has(l.code) && offered.get(l.code).transcribe !== false)
		.map((l) => ({ ...l, translate: l.translate && !!offered.get(l.code).translate }));
	if (!languages.length) return { ...FALLBACK_CATALOG, ...flags };
	const maxOutputs = Number.isInteger(raw.maxOutputs) && raw.maxOutputs > 0 ? Math.min(raw.maxOutputs, LANGS_MAX) : LANGS_MAX;
	return { languages, hub: HUB_LANGUAGE, maxOutputs, ...flags };
}

/** The profanity filter only works for some spoken languages; no source yet is not blocked. */
export function isProfanitySupported(source, catalog = FALLBACK_CATALOG) {
	if (!source) return true;
	const sources = Array.isArray(catalog?.profanitySources) ? catalog.profanitySources : PROFANITY_SOURCES;
	return sources.includes(String(source).toLowerCase());
}

/** English first, then by name. */
function byDisplayOrder(a, b) {
	if (a.code === HUB_LANGUAGE) return -1;
	if (b.code === HUB_LANGUAGE) return 1;
	return a.name.localeCompare(b.name);
}

function byCode(catalog, code) {
	if (!code) return null;
	const lower = String(code).toLowerCase();
	return (catalog?.languages || []).find((l) => l.code.toLowerCase() === lower) || null;
}

let displayNames = null;
function getDisplayNames() {
	if (displayNames !== null) return displayNames;
	try {
		displayNames = typeof Intl !== 'undefined' && Intl.DisplayNames
			? new Intl.DisplayNames(['en'], { type: 'language' })
			: false;
	} catch {
		displayNames = false;
	}
	return displayNames;
}

/**
 * Human name for a language code ("es" -> "Spanish"). Prefers the catalog
 * (core's names), then Intl.DisplayNames, then the raw code.
 */
export function languageName(code, catalog = FALLBACK_CATALOG) {
	if (!code) return '';
	const entry = byCode(catalog, code) || byCode(FALLBACK_CATALOG, code);
	if (entry) return entry.name;
	if (EXTRA_NAMES[String(code).toLowerCase()]) return EXTRA_NAMES[String(code).toLowerCase()];
	const names = getDisplayNames();
	if (names) {
		try {
			const name = names.of(code);
			if (name && name !== code) return name;
		} catch {
			// invalid tag: fall through to the raw code
		}
	}
	return code;
}

/** Picker options for the languages a stream can be spoken in, English first. */
export function sourceOptions(catalog = FALLBACK_CATALOG) {
	const anyPair = catalog?.nonEnglishPairs === true;
	return (catalog.languages || [])
		.filter((l) => l.transcribe)
		.map((l) => ({
			code: l.code,
			name: l.name,
			hint: l.translate || anyPair ? 'Translation available' : 'Captions only',
		}))
		.sort(byDisplayOrder);
}

/** True when the spoken language can have translated tracks at all. */
export function sourceCanTranslate(source, catalog = FALLBACK_CATALOG) {
	const spoken = byCode(catalog, source);
	if (!spoken || !spoken.transcribe) return false;
	return catalog?.nonEnglishPairs === true || !!spoken.translate;
}

/** Speechmatics translates this pair itself: English on one side, both translatable. */
export function speechmaticsTranslates(source, target, catalog = FALLBACK_CATALOG) {
	const a = byCode(catalog, source);
	const b = byCode(catalog, target);
	if (!a || !b) return false;
	if (a.code === b.code) return true;
	return a.translate && b.translate && (a.code === HUB_LANGUAGE || b.code === HUB_LANGUAGE);
}

/**
 * A translated track Gemini handles automatically: only possible when core
 * offers non-English pairs and Speechmatics can't do the pair itself.
 */
export function needsGemini(source, target, catalog = FALLBACK_CATALOG) {
	if (catalog?.nonEnglishPairs !== true || !source || !target) return false;
	if (String(source).toLowerCase() === String(target).toLowerCase()) return false;
	return !speechmaticsTranslates(source, target, catalog);
}

/**
 * Languages a session spoken in `source` may translate into (the source
 * itself excluded): every other translatable language from English, English
 * from any other translatable language, nothing from a transcribe-only one.
 * With `nonEnglishPairs`, every other catalog language.
 */
export function allowedTargets(source, catalog = FALLBACK_CATALOG) {
	const spoken = byCode(catalog, source);
	if (!spoken || !spoken.transcribe) return [];
	if (catalog?.nonEnglishPairs === true) return catalog.languages.filter((l) => l.code !== spoken.code).map((l) => l.code);
	if (!spoken.translate) return [];
	if (spoken.code === HUB_LANGUAGE) return catalog.languages.filter((l) => l.translate && l.code !== HUB_LANGUAGE).map((l) => l.code);
	return byCode(catalog, HUB_LANGUAGE)?.translate ? [HUB_LANGUAGE] : [];
}

/** Every track a session spoken in `source` may publish: the source, then its translations. */
export function allowedOutputsFor(source, catalog = FALLBACK_CATALOG) {
	const spoken = byCode(catalog, source);
	if (!spoken || !spoken.transcribe) return source ? [source] : [];
	return [spoken.code, ...allowedTargets(spoken.code, catalog)];
}

/**
 * Why `output` may not be published for a session spoken in `source`, or ''
 * when it may. User-facing, names the languages.
 */
export function outputDisallowedReason(source, output, catalog = FALLBACK_CATALOG) {
	if (!source || !output) return '';
	if (source.toLowerCase() === output.toLowerCase()) return '';
	const spoken = byCode(catalog, source);
	const spokenName = languageName(source, catalog);
	if (!spoken || !spoken.transcribe) return `${spokenName} is not a supported spoken language.`;
	if (catalog?.nonEnglishPairs === true) {
		return byCode(catalog, output) ? '' : `${languageName(output, catalog)} isn't available for captions.`;
	}
	if (!spoken.translate) {
		return `${spokenName} can be captioned but not translated. Captions will be in ${spokenName}.`;
	}
	const target = byCode(catalog, output);
	if (!target) return `${languageName(output, catalog)} isn't available for captions.`;
	if (spoken.code === HUB_LANGUAGE) {
		if (target.translate) return '';
		return `${target.name} can be captioned but not translated into.`;
	}
	if (target.code === HUB_LANGUAGE && target.translate) return '';
	return TRANSLATION_RULE;
}

/**
 * Options for the output-language picker given a source: every translatable
 * language, with `disabledReason` set on the ones the hub rules exclude.
 */
export function outputOptions(source, catalog = FALLBACK_CATALOG) {
	const spoken = byCode(catalog, source);
	const anyPair = catalog?.nonEnglishPairs === true && !!spoken?.transcribe;
	let pool;
	if (anyPair) pool = catalog.languages;
	else if (spoken && spoken.translate) pool = catalog.languages.filter((l) => l.translate);
	else pool = catalog.languages.filter((l) => l.code === (spoken?.code || source));
	return pool
		.map((l) => {
			const gemini = needsGemini(source, l.code, catalog);
			let hint = 'Translation';
			if (l.code === (spoken?.code || source)) hint = 'Spoken language';
			else if (gemini) hint = 'Translation · Gemini';
			return {
				code: l.code,
				name: l.name,
				disabledReason: outputDisallowedReason(source, l.code, catalog),
				hint,
				tag: gemini ? 'Gemini' : '',
			};
		})
		// Addable languages first so the one or two the rules allow are not
		// buried under a wall of disabled entries; English first within each group.
		.sort((a, b) => (Number(!!a.disabledReason) - Number(!!b.disabledReason)) || byDisplayOrder(a, b));
}

/**
 * The `langs` rules for one source, so a form can block Save on any of them:
 * 1..maxOutputs unique valid tags, each publishable for the source, and the
 * source itself among them unless the catalog says `sourceTrackOptional`
 * (an older core rejects `langs` without it).
 * Returns { valid, error, code, invalid: [{ code, reason }] } with `code` one
 * of 'empty' | 'too_many' | 'duplicate' | 'bad_tag' | 'not_allowed' | 'source_missing'.
 */
export function validateLangs(source, langs, { catalog = FALLBACK_CATALOG, maxOutputs } = {}) {
	const max = maxOutputs || catalog.maxOutputs || LANGS_MAX;
	const list = Array.isArray(langs) ? langs : [];
	const invalid = source
		? list.map((code) => ({ code, reason: outputDisallowedReason(source, code, catalog) })).filter((x) => x.reason)
		: [];
	const fail = (code, error) => ({ valid: false, code, error, invalid });
	if (list.length === 0) return fail('empty', 'Add at least one caption language.');
	if (list.length > max) return fail('too_many', `You can publish up to ${max} languages.`);
	if (new Set(list.map((l) => String(l).toLowerCase())).size !== list.length) return fail('duplicate', 'Each caption language can only be added once.');
	if (list.some((l) => !isValidLanguageTag(l))) return fail('bad_tag', 'One of the caption languages is not a valid language code.');
	if (invalid.length) return fail('not_allowed', invalid[0].reason);
	if (source && catalog.sourceTrackOptional !== true && !list.some((l) => String(l).toLowerCase() === String(source).toLowerCase())) {
		const name = languageName(source, catalog);
		return fail('source_missing', `Include ${name}: captions must include the spoken language.`);
	}
	return { valid: true, error: '', code: '', invalid };
}

const BCP47 = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

export function isValidLanguageTag(code) {
	return typeof code === 'string' && code.length >= 2 && code.length <= 35 && BCP47.test(code);
}

/**
 * Mirrors the API rules so the form can reject bad input before a round-trip:
 * a transcribable source; `validateLangs` for the outputs; delay 0 to 30.
 * `langsCode` names the failed `langs` rule (see validateLangs).
 */
export function validateSettings({ source, langs, delaySec }, catalog = FALLBACK_CATALOG) {
	const errors = {};
	if (!source) {
		errors.source = 'Choose the language spoken on the stream.';
	} else if (!isValidLanguageTag(source)) {
		errors.source = 'The source language must be a valid language code.';
	} else if (!byCode(catalog, source)?.transcribe) {
		errors.source = `${languageName(source, catalog)} is not a supported spoken language.`;
	}

	const langsCheck = validateLangs(errors.source ? '' : source, langs, { catalog });
	if (!langsCheck.valid) {
		errors.langs = langsCheck.error;
		errors.langsCode = langsCheck.code;
	}

	const delay = Number(delaySec);
	if (!Number.isFinite(delay) || delay < DELAY_MIN || delay > DELAY_MAX) {
		errors.delaySec = `Broadcast delay must be between ${DELAY_MIN} and ${DELAY_MAX} seconds.`;
	}

	return { valid: Object.keys(errors).length === 0, errors };
}

export function clampDelay(value) {
	const n = Math.round(Number(value));
	if (!Number.isFinite(n)) return DELAY_MIN;
	return Math.min(DELAY_MAX, Math.max(DELAY_MIN, n));
}

/** True when a status payload says core could not reach the captions service. */
export function isUnreachableStatus(status) {
	return !!status && (status.error === 'unreachable' || status.unreachable === true);
}

/** Epoch ms from an epoch-ms number or an ISO string; NaN otherwise. */
export function toMs(value) {
	if (typeof value === 'number') return value;
	if (typeof value === 'string' && value) return Date.parse(value);
	return NaN;
}

/**
 * The caption window for a flow: `{ state, startedAt, durationSec, expiresAt,
 * remainingSec, endedAt, endedReason }` with `state` one of
 * 'idle' | 'running' | 'stopped' | 'expired'. Works from `status.window`
 * (core with windows), the list `summary.window`, or falls back to
 * `status.running` for an older core (an unlimited, always-on session).
 */
export function windowOf(status, flow = null) {
	const raw = status && !isUnreachableStatus(status) ? status.window : (flow?.summary?.window ?? null);
	const configured = status && !isUnreachableStatus(status) ? !!status.configured : !!flow?.configured;
	if (!configured) return { state: 'idle', durationSec: null, expiresAt: null, remainingSec: null, endedAt: null, endedReason: null, startedAt: null };
	if (raw && typeof raw === 'object') {
		const state = ['running', 'stopped', 'expired', 'idle'].includes(raw.state) ? raw.state : (raw.state ? 'idle' : null);
		return {
			state: state || (status?.running ? 'running' : 'idle'),
			startedAt: Number.isFinite(toMs(raw.startedAt)) ? toMs(raw.startedAt) : null,
			durationSec: Number.isFinite(Number(raw.durationSec)) && raw.durationSec !== null ? Number(raw.durationSec) : null,
			expiresAt: Number.isFinite(toMs(raw.expiresAt)) ? toMs(raw.expiresAt) : null,
			remainingSec: Number.isFinite(Number(raw.remainingSec)) && raw.remainingSec !== null ? Number(raw.remainingSec) : null,
			endedAt: Number.isFinite(toMs(raw.endedAt)) ? toMs(raw.endedAt) : null,
			endedReason: raw.endedReason || null,
		};
	}
	if (status && !isUnreachableStatus(status)) {
		return {
			state: status.running ? 'running' : (status.state === 'expired' ? 'expired' : status.state === 'stopped' ? 'stopped' : 'idle'),
			startedAt: null, durationSec: null, expiresAt: null, remainingSec: null, endedAt: null, endedReason: null,
		};
	}
	return { state: 'idle', startedAt: null, durationSec: null, expiresAt: null, remainingSec: null, endedAt: null, endedReason: null };
}

/**
 * Detail for a window's `endedReason`: 'stopped', 'expired', 'hours used up'
 * (core ended it because the plan's pool ran dry). A window only ends by
 * Start/Stop, its timer or the pool; the stream going offline never ends one.
 */
export function endedReasonLabel(reason) {
	switch (reason) {
		case 'pool_exhausted':
			return 'hours used up';
		case 'stopped':
		case 'expired':
			return reason;
		default:
			return reason ? String(reason).replace(/_/g, ' ') : '';
	}
}

/** Badge key for a window that is not running: 'off' (idle), 'expired' or 'stopped'. */
function endedKey(win) {
	if (win?.state === 'expired') return 'expired';
	if (win?.state === 'stopped') return 'stopped';
	return 'off';
}

/** Headline label for a configured window that is not running. */
export function offLabel(win) {
	if (win?.endedReason === 'pool_exhausted') return 'Stopped — hours used up';
	switch (endedKey(win)) {
		case 'expired':
			return 'Session ended (time limit reached)';
		case 'stopped':
			return 'Stopped';
		default:
			return 'Captions off';
	}
}

/** Per-language badge for a window that is not running. */
function endedBadge(win, name) {
	const key = endedKey(win);
	if (win?.endedReason === 'pool_exhausted') {
		return { key, name, label: 'Stopped', detail: 'hours used up', tone: 'muted' };
	}
	if (key === 'expired') return { key, name, label: 'Session ended', tone: 'muted', tooltip: 'The time limit was reached.' };
	if (key === 'stopped') return { key, name, label: 'Stopped', tone: 'muted' };
	return { key, name, label: 'Captions off', tone: 'muted' };
}

const startingBadge = (name) => ({ key: 'starting', name, label: 'Starting', tone: 'warning', spinner: true });
const waitingStreamBadge = (name) => ({ key: 'waiting-stream', name, label: 'Waiting for stream', tone: 'warning', tooltip: WAITING_FOR_STREAM_HELP });

/**
 * Per-language badge. Captions run for the whole window from Start whether
 * or not anyone watches, so `status.active` is advisory and never read here.
 *   window idle                                   -> Captions off
 *   window expired / stopped                      -> Session ended / Stopped
 *   running, no fresh production, < 60s in        -> Starting
 *   running, no production (>= 60s) or > 45s old  -> Waiting for stream
 *   running, fresh production, edge <= 60s behind -> Live
 *   running, otherwise                            -> Stalled
 * "60s in" counts from `window.startedAt`, or from `restartedAt` (a settings
 * save or Apply now restarted the runner) when that is later. `now` is
 * server-adjusted time (Date.now() + skew). `unknown` covers an unreachable
 * captions service; `configured: false` a never-set-up flow.
 */
export function languageBadge(lang, status, now = Date.now(), { unknown = false, configured = true, catalog = FALLBACK_CATALOG, flow = null, restartedAt = null } = {}) {
	const name = languageName(lang, catalog);
	if (!configured) return { key: 'unconfigured', name, label: 'Not configured', tone: 'muted' };
	if (unknown || isUnreachableStatus(status)) return { key: 'unknown', name, label: 'Unknown', tone: 'warning' };

	const win = windowOf(status, flow);
	if (win.state !== 'running') return endedBadge(win, name);

	const since = Math.max(Number.isFinite(win.startedAt) ? win.startedAt : -Infinity, Number.isFinite(restartedAt) ? restartedAt : -Infinity);
	const production = status?.production && typeof status.production === 'object' ? status.production : null;
	const atMs = production ? toMs(production.at) : NaN;
	const productionFresh = Number.isFinite(atMs) && now - atMs <= PRODUCTION_MAX_AGE_MS;
	// Without a known start (older core) missing production can only read Starting.
	const inGrace = Number.isFinite(since) ? now - since < STARTING_GRACE_MS : !production;
	if (!production || !productionFresh) return inGrace ? startingBadge(name) : waitingStreamBadge(name);

	// Edge: production.langs[lang].edge (epoch ms), else the per-language edgeAgeSec core computes.
	const entry = (Array.isArray(status.languages) ? status.languages : []).find((l) => String(l.lang).toLowerCase() === String(lang).toLowerCase());
	const langsMap = production.langs && typeof production.langs === 'object' ? production.langs : {};
	const edgeKey = Object.keys(langsMap).find((k) => k.toLowerCase() === String(lang).toLowerCase());
	let edgeMs = edgeKey ? toMs(langsMap[edgeKey]?.edge) : NaN;
	if (!Number.isFinite(edgeMs) && Number.isFinite(Number(entry?.edgeAgeSec)) && entry?.edgeAgeSec !== null) {
		edgeMs = now - Number(entry.edgeAgeSec) * 1000;
	}
	if (Number.isFinite(edgeMs) && atMs - edgeMs <= EDGE_MAX_LAG_MS) return { key: 'live', name, label: 'Live', tone: 'success' };
	// A language with no cue yet right after a (re)start is still starting.
	if (!Number.isFinite(edgeMs) && inGrace) return startingBadge(name);
	return { key: 'stalled', name, label: 'Stalled', tone: 'warning' };
}

/**
 * One headline state for a flow, used by the list card and the manage
 * header. Prefers, in order: `flow.streamDeleted`, a full status payload, the
 * list `summary` core attaches to each flow, then the flow's own flags.
 * Keys: stream-removed, unconfigured, unknown, off, expired, stopped,
 * starting, waiting-stream, live, stalled, and `running` (a list summary
 * that says running but carries no production to tell the rest apart).
 */
export function summarizeFlowStatus(flow, status = null, now = Date.now(), { restartedAt = null } = {}) {
	if (flow?.streamDeleted) {
		return { key: 'stream-removed', label: 'Linked livestream removed', tone: 'error' };
	}
	if (!flow?.configured && !status?.configured) {
		return { key: 'unconfigured', label: 'Not configured', tone: 'muted' };
	}
	if (status && isUnreachableStatus(status)) {
		return { key: 'unknown', label: 'Unknown', tone: 'warning' };
	}

	if (!status) {
		// List view: core's lightweight summary. `undefined` means an older core
		// without summaries (the caller fetches status instead); `null` means core
		// could not reach the captions service for this flow.
		if (flow && flow.summary !== undefined) {
			const summary = flow.summary;
			if (!summary) return { key: 'unknown', label: 'Unknown', tone: 'warning' };
			if (!summary.window) {
				return summary.running ? { key: 'running', label: 'Running', tone: 'muted' } : { key: 'off', label: 'Captions off', tone: 'muted' };
			}
			const win = windowOf(null, flow);
			if (win.state !== 'running') return { key: endedKey(win), label: offLabel(win), tone: 'muted' };
			// Core may attach the runner's production to the summary; without it
			// the list can only say the window is on.
			if (summary.production === undefined) return { key: 'running', label: 'Running', tone: 'muted' };
			const pseudo = { configured: true, window: summary.window, production: summary.production, config: { langs: flow.langs || [] } };
			return summarizeFlowStatus(flow, pseudo, now, { restartedAt });
		}
		return { key: 'unknown', label: 'Checking', tone: 'muted' };
	}

	const win = windowOf(status, flow);
	if (win.state !== 'running') return { key: endedKey(win), label: offLabel(win), tone: 'muted' };
	const langs = (status.config?.langs || flow?.langs || []).map((l) => (typeof l === 'string' ? l : l.lang));
	const badges = langs.map((l) => languageBadge(l, status, now, { flow, restartedAt }));
	if (badges.some((b) => b.key === 'waiting-stream')) return { key: 'waiting-stream', label: 'Waiting for stream', tone: 'warning', tooltip: WAITING_FOR_STREAM_HELP };
	if (badges.some((b) => b.key === 'live')) return { key: 'live', label: 'Live', tone: 'success' };
	if (badges.some((b) => b.key === 'stalled')) return { key: 'stalled', label: 'Stalled', tone: 'warning' };
	return { key: 'starting', label: 'Starting', tone: 'warning' };
}

/** Codes currently live according to a status payload (badge rule). */
export function liveLanguages(status, now = Date.now(), flow = null) {
	if (!status || isUnreachableStatus(status)) return [];
	const langs = (status.config?.langs || (Array.isArray(status.languages) ? status.languages.map((l) => l.lang) : []));
	return langs.filter((l) => languageBadge(l, status, now, { flow }).key === 'live');
}

/** "42:10", "1:02:03"; hours only when needed. */
export function formatCountdown(totalSec) {
	const s = Math.max(0, Math.floor(Number(totalSec) || 0));
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const sec = s % 60;
	const mm = h ? String(m).padStart(2, '0') : String(m);
	return `${h ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** "2m 10s", "45s", "1h 05m"; for ages and coarse remaining time. */
export function formatDuration(totalSec) {
	const s = Math.max(0, Math.round(Number(totalSec) || 0));
	if (s < 60) return `${s}s`;
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	if (h) return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
	const sec = s % 60;
	return sec ? `${m}m ${String(sec).padStart(2, '0')}s` : `${m}m`;
}

/** Label for a duration preset value (900 -> "15 min", null -> "Unlimited"). */
export function durationLabel(sec) {
	if (sec === null || sec === undefined) return 'Unlimited';
	const preset = DURATION_PRESETS.find((p) => p.value === Number(sec));
	return preset ? preset.label : formatDuration(sec);
}

/** Local wall-clock "HH:MM" for an epoch-ms timestamp. */
export function formatClock(ms) {
	if (!Number.isFinite(ms)) return '';
	try {
		return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	} catch {
		const d = new Date(ms);
		return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
	}
}

/** "English", "English and Spanish", "English, Spanish and French". */
export function joinLanguageNames(codes, catalog = FALLBACK_CATALOG) {
	const names = (codes || []).map((c) => languageName(c, catalog));
	if (names.length <= 1) return names.join('');
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
